// Export Utilities for Diagrams and PDFs
// Provides export functionality for 5-Why, Ishikawa, FTA, and 8D reports
// ============================================================

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Export a DOM element as a PNG image
 */
export const exportAsImage = async (
    elementId: string,
    fileName: string = 'diagram',
    format: 'png' | 'jpeg' = 'png'
): Promise<void> => {
    const element = document.getElementById(elementId);
    if (!element) {
        console.error(`Element with id "${elementId}" not found`);
        return;
    }

    try {
        const canvas = await html2canvas(element, {
            backgroundColor: '#ffffff',
            scale: 2, // Higher resolution
            useCORS: true,
            logging: false,
        });

        const dataUrl = canvas.toDataURL(`image/${format}`);
        const link = document.createElement('a');
        link.download = `${fileName}.${format}`;
        link.href = dataUrl;
        link.click();
    } catch (error) {
        console.error('Error exporting image:', error);
        throw error;
    }
};

/**
 * Export a DOM element as a PDF
 */
export const exportAsPDF = async (
    elementId: string,
    fileName: string = 'document',
    options?: {
        orientation?: 'portrait' | 'landscape';
        title?: string;
        includeTimestamp?: boolean;
    }
): Promise<void> => {
    const element = document.getElementById(elementId);
    if (!element) {
        console.error(`Element with id "${elementId}" not found`);
        return;
    }

    const {
        orientation = 'portrait',
        title,
        includeTimestamp = true,
    } = options || {};

    try {
        const canvas = await html2canvas(element, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            logging: false,
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
            orientation,
            unit: 'mm',
            format: 'a4',
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const contentWidth = pageWidth - (margin * 2);

        // Calculate image dimensions to fit page
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        let yPosition = margin;

        // Add title if provided
        if (title) {
            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'bold');
            pdf.text(title, margin, yPosition + 5);
            yPosition += 15;
        }

        // Add timestamp
        if (includeTimestamp) {
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(128, 128, 128);
            const timestamp = new Date().toLocaleString();
            pdf.text(`Generated: ${timestamp}`, margin, yPosition);
            pdf.setTextColor(0, 0, 0);
            yPosition += 10;
        }

        // Add image, handle multi-page if needed
        if (imgHeight + yPosition > pageHeight - margin) {
            // Scale down to fit on one page
            const scaleFactor = (pageHeight - yPosition - margin) / imgHeight;
            pdf.addImage(
                imgData,
                'PNG',
                margin,
                yPosition,
                imgWidth * scaleFactor,
                imgHeight * scaleFactor
            );
        } else {
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
        }

        pdf.save(`${fileName}.pdf`);
    } catch (error) {
        console.error('Error exporting PDF:', error);
        throw error;
    }
};

import { generate8DReportPDF } from './pdfExportUtils';

// ... other imports if needed ...

/**
 * Generate a full 8D report PDF
 */
export { generate8DReportPDF };

/**
 * Export 5-Why diagram as image
 */
export const export5WhyDiagram = async (
    elementId: string = 'five-why-diagram'
): Promise<void> => {
    await exportAsImage(elementId, `5Why_Analysis_${Date.now()}`);
};

/**
 * Export Ishikawa (Fishbone) diagram as image
 */
export const exportIshikawaDiagram = async (
    elementId: string = 'ishikawa-diagram'
): Promise<void> => {
    await exportAsImage(elementId, `Ishikawa_Diagram_${Date.now()}`);
};

/**
 * Export FTA diagram as image
 */
export const exportFTADiagram = async (
    elementId: string = 'fta-diagram'
): Promise<void> => {
    await exportAsImage(elementId, `FTA_Diagram_${Date.now()}`);
};

/**
 * Export any diagram with custom filename
 */
export const exportDiagram = async (
    elementId: string,
    fileName: string,
    format: 'png' | 'pdf' = 'png'
): Promise<void> => {
    if (format === 'pdf') {
        await exportAsPDF(elementId, fileName);
    } else {
        await exportAsImage(elementId, fileName);
    }
};

export default {
    exportAsImage,
    exportAsPDF,
    generate8DReportPDF,
    export5WhyDiagram,
    exportIshikawaDiagram,
    exportFTADiagram,
    exportDiagram,
};
